/**
 * BluezoneRecipeEmbed - Vollstaendige Rezept-Einbettung in Artikeln
 *
 * Zeigt das komplette Rezept mit allen Details an:
 * - Hero-Bild mit Overlay
 * - Metadaten-Badges
 * - GoodLife-Faktor Box
 * - Meal-Prep Toggle
 * - Zutatenliste
 * - Crafting Steps
 * - Crafting Tipp
 */

import React, { useState, useEffect } from 'react';
import { Clock, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import { getRecipeById } from '../../services/bluezoneRecipeService';
import type { BluezoneRecipe } from '../../types/bluezoneTypes';

interface BluezoneRecipeEmbedProps {
  recipeId: string;
}

export function BluezoneRecipeEmbed({ recipeId }: BluezoneRecipeEmbedProps) {
  const [recipe, setRecipe] = useState<BluezoneRecipe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mealPrepDays, setMealPrepDays] = useState<2 | 3>(2);
  const [showSteps, setShowSteps] = useState(true);
  const [checkedIngredients, setCheckedIngredients] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (recipeId) {
      loadRecipe();
    } else {
      setError('Keine Rezept-ID angegeben');
      setIsLoading(false);
    }
  }, [recipeId]);

  const loadRecipe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('[BluezoneRecipeEmbed] Loading recipe:', recipeId);
      const loadedRecipe = await getRecipeById(recipeId);
      console.log('[BluezoneRecipeEmbed] Recipe loaded:', loadedRecipe ? 'success' : 'not found');
      if (!loadedRecipe) {
        setError('Rezept nicht gefunden');
      }
      setRecipe(loadedRecipe);
    } catch (err) {
      console.error('[BluezoneRecipeEmbed] Error loading recipe:', err);
      setError('Fehler beim Laden des Rezepts');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleIngredient = (index: number) => {
    const newChecked = new Set(checkedIngredients);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedIngredients(newChecked);
  };

  if (isLoading) {
    return (
      <div className="my-8 bg-gray-50 rounded-2xl p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-amber-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="my-8 bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
        <p className="text-amber-800 mb-3">{error || 'Rezept konnte nicht geladen werden'}</p>
        <button
          onClick={loadRecipe}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm font-medium"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="my-8 bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      {/* Hero Image */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        {recipe.heroImageUrl ? (
          <img
            src={recipe.heroImageUrl}
            alt={recipe.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-amber-100 to-orange-200 flex items-center justify-center">
            <span className="text-6xl">🥗</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <h3 className="text-xl md:text-2xl font-bold mb-1">{recipe.title}</h3>
          <p className="text-white/80 text-sm">{recipe.subtitle}</p>
        </div>
      </div>

      <div className="p-6">
        {/* Metadata Badges */}
        <div className="flex flex-wrap gap-2 mb-6">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm">
            <Clock className="w-3.5 h-3.5" />
            {recipe.prepTime}
          </span>
          <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm capitalize">
            🌱 {recipe.recipeType.replace('_', '-')}
          </span>
          <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm">
            {recipe.regionEmoji} {recipe.regionLabel}
          </span>
          <span className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm">
            {recipe.energyEmoji} {recipe.energyLevel}
          </span>
        </div>

        {/* GoodLife Factor Box */}
        <div className="bg-blue-50 rounded-xl p-5 mb-6 border-l-4 border-blue-500">
          <h4 className="font-semibold text-blue-900 mb-3">GoodLife Faktor</h4>
          <div className="space-y-2 text-sm text-blue-800">
            <p><span className="font-medium">Hauptbenefit:</span> {recipe.goodLifeFactor.mainBenefit}</p>
            <p><span className="font-medium">GoodLife:</span> {recipe.goodLifeFactor.goodLifeBenefit}</p>
            <p><span className="font-medium">GoodWork:</span> {recipe.goodLifeFactor.goodWorkBenefit}</p>
          </div>
        </div>

        {/* Meal-Prep Toggle */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-lg">🛒</span>
            <h4 className="font-semibold text-gray-900">Zutaten</h4>
            <span className="text-sm text-gray-500">(fuer 1 Person)</span>
          </div>
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-full">
            <button
              onClick={() => setMealPrepDays(2)}
              className={`px-3 py-1 text-sm rounded-full transition ${
                mealPrepDays === 2
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              2 Tage
            </button>
            <button
              onClick={() => setMealPrepDays(3)}
              className={`px-3 py-1 text-sm rounded-full transition ${
                mealPrepDays === 3
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              3 Tage
            </button>
          </div>
        </div>

        {/* Ingredients List */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <div className="space-y-2">
            {recipe.ingredients.map((ingredient, index) => (
              <label
                key={index}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <input
                  type="checkbox"
                  checked={checkedIngredients.has(index)}
                  onChange={() => toggleIngredient(index)}
                  className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <span
                  className={`flex-1 text-sm ${
                    checkedIngredients.has(index)
                      ? 'text-gray-400 line-through'
                      : 'text-gray-700'
                  }`}
                >
                  <span className="font-medium">
                    {mealPrepDays === 2 ? ingredient.amount2Days : ingredient.amount3Days}{' '}
                    {ingredient.unit}
                  </span>{' '}
                  {ingredient.name}
                  {ingredient.isOptional && (
                    <span className="text-gray-400 text-xs ml-1">(optional)</span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Crafting Steps (collapsible) */}
        <div className="mb-6">
          <button
            onClick={() => setShowSteps(!showSteps)}
            className="w-full flex items-center justify-between py-3 border-b border-gray-100"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">🥣</span>
              <h4 className="font-semibold text-gray-900">Crafting Steps</h4>
            </div>
            {showSteps ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showSteps && (
            <div className="pt-4 space-y-4">
              {recipe.craftingSteps.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {step.stepNumber}
                  </div>
                  <div className="flex-1 pt-0.5">
                    <h5 className="font-medium text-gray-900 text-sm mb-1">{step.title}</h5>
                    <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                    {step.duration && (
                      <span className="inline-block mt-1 text-xs text-gray-400">
                        ca. {step.duration}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Crafting Tipp */}
        <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-5 h-5 text-amber-600" />
            <h4 className="font-semibold text-amber-900">Crafting Tipp</h4>
          </div>
          <p className="text-amber-800 text-sm leading-relaxed">{recipe.craftingTipp}</p>
        </div>
      </div>
    </div>
  );
}

export default BluezoneRecipeEmbed;
